import { useState } from "react"
import { App as AntdApp, Button, Table, Tag, Tooltip } from "antd"
import type { TableColumnsType } from "antd"
import { ChevronRight, Play } from "lucide-react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type { CollectionRun } from "@csbi/contracts"
import { formatTimestamp } from "@csbi/viz-core"
import { fetchRuns, triggerRun } from "../../../features/tiles/api.js"
import { ApiError } from "../../../shared/api/request.js"
import { PageHeader } from "../../../shared/components/page-header.js"
import { InfoHint, REJECTED_MEANING, RUN_SCOPE_MEANING, RunDetailDrawer, withHint } from "./run-detail.js"

/**
 * ⚠ **Fixed on 2026-09-10**: this column had **never had a data source** — `points_rejected` is not on the
 * `collection_run` table (only on `collection_task`), the API did not query it, and so it always rendered a grey
 * "0". An alarm that cannot go off is worse than no alarm, because people assume it is watching.
 * The collector now aggregates it from the task table. The same fix also rescued the page from an "API in
 * snake_case, frontend in camelCase" mismatch that made it throw the moment it opened.
 */
export function RunsPage() {
  const [openRun, setOpenRun] = useState<string | null>(null)
  const { message, modal } = AntdApp.useApp()
  const qc = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ["ops", "runs"],
    queryFn: ({ signal }) => fetchRuns(signal),
    staleTime: 5_000,
    refetchInterval: (q) =>
      q.state.data?.runs.some((r) => r.finishedAt === null) ? 5_000 : 30_000,
  })

  /**
   * ⚠ **This request hangs until the whole run finishes** (measured at 4m10s for one local deployment and 14
   * tasks), because upstream is a synchronous `await executeRun(...)`.
   */
  const run = useMutation({
    mutationFn: () => triggerRun(),
    onMutate: () => {
      void message.info("Collection started — watch the table, it refreshes while a run is in flight.", 5)
      setTimeout(() => void qc.invalidateQueries({ queryKey: ["ops", "runs"] }), 1_500)
    },
    onSuccess: (r) => {
      void message.success(
        r.tasksFailed
          ? `Collection finished with ${r.tasksFailed} failed task(s) — open the run to see which.`
          : "Collection finished.",
        6,
      )
      void qc.invalidateQueries({ queryKey: ["ops", "runs"] })
      void qc.invalidateQueries({ queryKey: ["tiles"] })
    },
    onError: (err) => {
      void qc.invalidateQueries({ queryKey: ["ops", "runs"] })
      void message.error(
        (err instanceof ApiError ? err.message : "The request failed.") +
          " The run may still be in progress — check the table before starting another.",
        10,
      )
    },
  })

  /**
   * ⚠ The wording may only state **what we actually know**: the module count (14), the concurrency (4), the rate
   * limit (5/hour) and that a scheduled run happens daily — all constants in the code. **No concrete duration**:
   * it depends on how large the customer's database is and where the last run got to, and the 4 minutes measured locally predicts nothing about production. Writing it down turns a coincidence into a promise.
   */
  const confirm = () =>
    modal.confirm({
      title: "Run collection now?",
      content:
        "This calls every deployment that has collection switched on: 14 modules each, at least one request " +
        "per module. Modules are fetched one at a time within a deployment, 4 deployments in parallel, so a " +
        "run takes minutes rather than seconds. Scheduled collection already does this daily, and manual runs " +
        "are limited to 5 per hour. The table updates while the run is in flight.",
      okText: "Run now",
      onOk: () => run.mutateAsync(),
    })

  const columns: TableColumnsType<CollectionRun> = [
    {
      title: "Started",
      dataIndex: "startedAt",
      key: "startedAt",
      width: 190,
      render: (v: string) => formatTimestamp(v),
    },
    { title: "Trigger", dataIndex: "trigger", key: "trigger", width: 100 },
    {
      /**
       * ⚠ This column answers "whose data is this row".
       */
      title: withHint("Covered", RUN_SCOPE_MEANING),
      dataIndex: "tenantsTotal",
      key: "tenantsTotal",
      width: 150,
      /**
       * ⚠ With a unit, not a bare number.
       */
      render: (n: number) => (
        <span>
          <span style={{ fontVariantNumeric: "tabular-nums" }}>{n}</span>
          <span style={{ color: "var(--csbi-ink-muted)" }}> deployment{n === 1 ? "" : "s"}</span>
        </span>
      ),
    },
    {
      title: "Tasks",
      key: "tasks",
      width: 130,
      align: "right",
      render: (_: unknown, r) => (
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          {r.tasksOk}/{r.tasksTotal}
        </span>
      ),
    },
    {
      title: "Points written",
      dataIndex: "pointsWritten",
      key: "pointsWritten",
      width: 120,
      align: "right",
      render: (n: number) => <span style={{ fontVariantNumeric: "tabular-nums" }}>{n.toLocaleString()}</span>,
    },
    {
      title: (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          Points rejected
          <InfoHint text={REJECTED_MEANING} />
        </span>
      ),
      dataIndex: "pointsRejected",
      key: "pointsRejected",
      width: 140,
      align: "right",
      render: (n: number) =>
        n > 0 ? (
          <Tooltip title={`${REJECTED_MEANING} Open the run to see which module.`}>
            <Tag color="error" style={{ marginInlineEnd: 0 }}>
              {n.toLocaleString()}
            </Tag>
          </Tooltip>
        ) : (
          <span style={{ fontVariantNumeric: "tabular-nums", color: "var(--csbi-ink-muted)" }}>0</span>
        ),
    },
    {
      title: "Finished",
      dataIndex: "finishedAt",
      key: "finishedAt",
      width: 190,
      render: (v: string | null) => (v ? formatTimestamp(v) : <Tag>running</Tag>),
    },
    {
      /**
       * ⚠ The whole row is clickable, but **"clickable" cannot be seen**: a table reads as read-only by default,
       * and a cursor change is something you learn after moving there. So there is an explicit entry point.
       */
      title: "",
      key: "open",
      width: 110,
      align: "right",
      render: (_: unknown, r) => (
        <Button type="link" size="small" style={{ paddingInline: 0 }} onClick={() => setOpenRun(r.id)}>
          Tasks
          <ChevronRight size={14} style={{ verticalAlign: "-3px" }} />
        </Button>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="Collection Runs"
        extra={
          <Button icon={<Play size={14} />} loading={run.isPending} onClick={confirm}>
            Run collection
          </Button>
        }
      />
      <Table<CollectionRun>
        size="small"
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={data?.runs ?? []}
        pagination={{ pageSize: 20, showSizeChanger: false }}
        onRow={(r) => ({ onClick: () => setOpenRun(r.id), style: { cursor: "pointer" } })}
      />
      <RunDetailDrawer runId={openRun} onClose={() => setOpenRun(null)} />
    </>
  )
}
